import type { AgentCommand, CommandRejectionReason, KeyModifiers, NodeTarget, TextPosition } from '../protocol';
import { isTextField, resolveCssCursor } from './cssCursor';

export interface NodeLookup {
    getNode(nodeId: number): Node | null;
}

export interface ScreenPoint {
    x: number;
    y: number;
}

export type ExecutableCommand = Extract<AgentCommand, { type: 'click' | 'double-click' | 'selection:set' | 'text:insert' | 'key:press' | 'scroll' | 'select:change' }>;

export type CursorKind = 'default' | 'pointer' | 'text' | 'not-allowed' | 'grab' | 'move';

const CURSOR_KINDS: Record<string, CursorKind> = {
    pointer: 'pointer',
    text: 'text',
    'vertical-text': 'text',
    'not-allowed': 'not-allowed',
    'no-drop': 'not-allowed',
    grab: 'grab',
    grabbing: 'grab',
    move: 'move',
};

export const BLOCKED_SELECTOR = '[data-cobrowse-block]';

export class CommandExecutor {
    private readonly remotelyEditedFields = new WeakSet<Element>();
    private notifiedFocus: Element | null = null;

    constructor(
        private readonly nodes: NodeLookup,
        private readonly targetDocument: Document = document
    ) {
        this.targetDocument.addEventListener('focusout', this.commitRemoteEdit, true);
    }

    destroy(): void {
        this.targetDocument.removeEventListener('focusout', this.commitRemoteEdit, true);
    }

    resolvePoint(target: NodeTarget): ScreenPoint | null {
        const element = this.elementOf(target.nodeId);

        if (!element) {
            return null;
        }

        const rect = element.getBoundingClientRect();

        return { x: rect.left + rect.width * target.ratioX, y: rect.top + rect.height * target.ratioY };
    }

    resolveCursor(target: NodeTarget): CursorKind {
        const element = this.elementOf(target.nodeId);
        const point = this.resolvePoint(target);

        if (!element || !point) {
            return 'default';
        }

        return CURSOR_KINDS[resolveCssCursor(element, point)] ?? 'default';
    }

    execute(command: ExecutableCommand): CommandRejectionReason | null {
        switch (command.type) {
            case 'click':
                return this.click(command.target);
            case 'double-click':
                return this.doubleClick(command.target);
            case 'selection:set':
                return this.setSelection(command.anchor, command.focus);
            case 'text:insert':
                return this.insertText(command.text);
            case 'key:press':
                return this.pressKey(command.key, command.code, command);
            case 'scroll':
                return this.scroll(command.nodeId, command.deltaX, command.deltaY);
            case 'select:change':
                return this.changeSelect(command.nodeId, command.value);
            case 'field:set-value':
                return this.setFieldValue(command.nodeId, command.value);
        }
    }

    private click(target: NodeTarget): CommandRejectionReason | null {
        const element = this.elementOf(target.nodeId);
        const point = this.resolvePoint(target);

        if (!element || !point) {
            return 'node-not-found';
        }

        if (element.closest(BLOCKED_SELECTOR)) {
            return 'blocked';
        }

        const pointer = { bubbles: true, cancelable: true, composed: true, clientX: point.x, clientY: point.y, button: 0, view: this.view };
        const pointerDetails = { ...pointer, pointerId: 1, pointerType: 'mouse', isPrimary: true };

        this.dispatchPointer(element, 'pointerover', pointerDetails);
        element.dispatchEvent(new MouseEvent('mouseover', pointer));
        this.dispatchPointer(element, 'pointerdown', { ...pointerDetails, buttons: 1 });
        const mouseDown = new MouseEvent('mousedown', { ...pointer, buttons: 1, detail: 1 });
        element.dispatchEvent(mouseDown);

        if (!mouseDown.defaultPrevented) {
            this.focusAt(element, point);
        }

        this.dispatchPointer(element, 'pointerup', pointerDetails);
        element.dispatchEvent(new MouseEvent('mouseup', { ...pointer, detail: 1 }));
        element.dispatchEvent(new MouseEvent('click', { ...pointer, detail: 1 }));

        return null;
    }

    private doubleClick(target: NodeTarget): CommandRejectionReason | null {
        const element = this.elementOf(target.nodeId);
        const point = this.resolvePoint(target);

        if (!element || !point) {
            return 'node-not-found';
        }

        if (element.closest(BLOCKED_SELECTOR)) {
            return 'blocked';
        }

        element.dispatchEvent(new MouseEvent('dblclick', { bubbles: true, cancelable: true, composed: true, clientX: point.x, clientY: point.y, detail: 2, view: this.view }));

        if (this.isTextField(element)) {
            element.select();
            return null;
        }

        this.selectWordAt(point);

        return null;
    }

    private setSelection(anchor: TextPosition, focus: TextPosition): CommandRejectionReason | null {
        const anchorNode = this.nodes.getNode(anchor.nodeId);
        const focusNode = this.nodes.getNode(focus.nodeId);
        const selection = this.view?.getSelection();

        if (!anchorNode || !focusNode || !selection) {
            return 'node-not-found';
        }

        selection.setBaseAndExtent(anchorNode, this.clampOffset(anchorNode, anchor.offset), focusNode, this.clampOffset(focusNode, focus.offset));

        return null;
    }

    private selectWordAt(point: ScreenPoint): void {
        const selection = this.view?.getSelection();

        if (!selection) {
            return;
        }

        this.placeCaretAt(point);
        selection.modify?.('move', 'backward', 'word');
        selection.modify?.('extend', 'forward', 'word');
    }

    private clampOffset(node: Node, offset: number): number {
        const length = node.nodeType === Node.TEXT_NODE ? node.textContent?.length ?? 0 : node.childNodes.length;

        return Math.min(Math.max(0, offset), length);
    }

    private insertText(text: string): CommandRejectionReason | null {
        const field = this.targetDocument.activeElement;

        if (!field || field === this.targetDocument.body) {
            return 'unsupported';
        }

        if (field.closest(BLOCKED_SELECTOR)) {
            return 'blocked';
        }

        if (this.targetDocument.execCommand?.('insertText', false, text)) {
            this.remotelyEditedFields.add(field);
            return null;
        }

        if (!this.isTextField(field)) {
            return 'unsupported';
        }

        const start = field.selectionStart ?? field.value.length;
        const end = field.selectionEnd ?? field.value.length;

        field.setRangeText(text, start, end, 'end');
        field.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: text }));
        this.remotelyEditedFields.add(field);

        return null;
    }

    private pressKey(key: string, code: string, modifiers: KeyModifiers): CommandRejectionReason | null {
        const activeElement = this.targetDocument.activeElement ?? this.targetDocument.body;
        const init = { key, code, bubbles: true, cancelable: true, composed: true, ...modifiers };
        const keyDown = new KeyboardEvent('keydown', init);

        activeElement.dispatchEvent(keyDown);

        if (!keyDown.defaultPrevented) {
            this.applyDefaultKeyAction(activeElement, key, modifiers);
        }

        activeElement.dispatchEvent(new KeyboardEvent('keyup', init));

        return null;
    }

    private applyDefaultKeyAction(activeElement: Element, key: string, modifiers: KeyModifiers): void {
        if (key === 'Backspace' || key === 'Delete') {
            this.deleteText(activeElement, key === 'Backspace' ? 'backward' : 'forward');
            return;
        }

        if (key.toLowerCase() === 'a' && (modifiers.metaKey || modifiers.ctrlKey)) {
            this.selectAll(activeElement);
            return;
        }

        if (key === 'Tab') {
            this.moveFocus(activeElement, modifiers.shiftKey ? -1 : 1);
            return;
        }

        if (key !== 'Enter') {
            return;
        }

        if (activeElement instanceof HTMLInputElement && activeElement.form) {
            activeElement.dispatchEvent(new Event('change', { bubbles: true }));
            activeElement.form.requestSubmit();
            return;
        }

        if (activeElement instanceof HTMLTextAreaElement || (activeElement as HTMLElement).isContentEditable) {
            this.insertText('\n');
        }
    }

    private selectAll(activeElement: Element): void {
        if (this.isTextField(activeElement)) {
            activeElement.select();
            return;
        }

        this.targetDocument.execCommand?.('selectAll');
    }

    private deleteText(field: Element, direction: 'backward' | 'forward'): void {
        if (this.targetDocument.execCommand?.(direction === 'backward' ? 'delete' : 'forwardDelete')) {
            this.remotelyEditedFields.add(field);
            return;
        }

        if (!this.isTextField(field)) {
            return;
        }

        const start = field.selectionStart ?? field.value.length;
        const end = field.selectionEnd ?? field.value.length;
        const rangeStart = start !== end ? start : Math.max(0, direction === 'backward' ? start - 1 : start);
        const rangeEnd = start !== end ? end : direction === 'backward' ? start : start + 1;

        field.setRangeText('', rangeStart, rangeEnd, 'end');
        field.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: direction === 'backward' ? 'deleteContentBackward' : 'deleteContentForward' }));
        this.remotelyEditedFields.add(field);
    }

    private moveFocus(activeElement: Element, step: number): void {
        const focusables = Array.from(this.targetDocument.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]')).filter(
            (element) => !element.hasAttribute('disabled') && element.getClientRects().length > 0
        );
        const nextIndex = focusables.indexOf(activeElement as HTMLElement) + step;

        const next = focusables.at(nextIndex % focusables.length);

        if (next) {
            this.focusElement(next);
        }
    }

    private scroll(nodeId: number | null, deltaX: number, deltaY: number): CommandRejectionReason | null {
        const origin = nodeId === null ? null : this.elementOf(nodeId);
        const container = origin ? this.findScrollContainer(origin, deltaX, deltaY) : null;

        if (container) {
            container.scrollBy({ left: deltaX, top: deltaY });
            return null;
        }

        this.view?.scrollBy({ left: deltaX, top: deltaY });

        return null;
    }

    private changeSelect(nodeId: number, value: string): CommandRejectionReason | null {
        const select = this.elementOf(nodeId);

        if (!(select instanceof HTMLSelectElement)) {
            return 'node-not-found';
        }

        if (select.closest(BLOCKED_SELECTOR)) {
            return 'blocked';
        }

        select.value = value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));

        return null;
    }

    private setFieldValue(nodeId: number, value: string): CommandRejectionReason | null {
        const field = this.elementOf(nodeId);

        if (!(field instanceof HTMLInputElement)) {
            return 'node-not-found';
        }

        if (field.closest(BLOCKED_SELECTOR)) {
            return 'blocked';
        }

        field.value = value;
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));

        return null;
    }

    private focusAt(element: Element, point: ScreenPoint): void {
        const focusable = element.closest<HTMLElement>('input, textarea, select, button, a[href], [tabindex], [contenteditable="true"]');

        if (!focusable) {
            this.blurActiveElement();
            return;
        }

        this.focusElement(focusable);

        if (this.isTextField(focusable)) {
            const end = focusable.value.length;
            focusable.setSelectionRange?.(end, end);
            return;
        }

        if (focusable.isContentEditable) {
            this.placeCaretAt(point);
        }
    }

    // Without system focus (the client looking at another window), browsers move focus silently:
    // replay the events apps react to, such as FormattedNumberInput switching to edit mode.
    private focusElement(element: HTMLElement): void {
        const previous = this.targetDocument.activeElement as HTMLElement | null;

        element.focus({ preventScroll: true });

        if (this.targetDocument.hasFocus() || this.notifiedFocus === element) {
            return;
        }

        if (previous && previous !== element && previous !== this.targetDocument.body) {
            this.dispatchFocusChange(previous, 'blur', 'focusout', element);
        }

        this.dispatchFocusChange(element, 'focus', 'focusin', previous === element ? null : previous);
        this.notifiedFocus = element;
    }

    private blurActiveElement(): void {
        const active = this.targetDocument.activeElement as HTMLElement | null;

        if (!active || active === this.targetDocument.body) {
            return;
        }

        active.blur();
        this.notifiedFocus = null;

        if (!this.targetDocument.hasFocus()) {
            this.dispatchFocusChange(active, 'blur', 'focusout', null);
        }
    }

    private dispatchFocusChange(element: HTMLElement, type: 'focus' | 'blur', bubblingType: 'focusin' | 'focusout', relatedTarget: Element | null): void {
        element.dispatchEvent(new FocusEvent(type, { relatedTarget }));
        element.dispatchEvent(new FocusEvent(bubblingType, { bubbles: true, relatedTarget }));
    }

    private placeCaretAt(point: ScreenPoint): void {
        const range = this.targetDocument.caretRangeFromPoint?.(point.x, point.y);
        const selection = this.view?.getSelection();

        if (!range || !selection) {
            return;
        }

        selection.removeAllRanges();
        selection.addRange(range);
    }

    private findScrollContainer(origin: Element, deltaX: number, deltaY: number): Element | null {
        for (let element: Element | null = origin; element; element = element.parentElement) {
            const style = this.view?.getComputedStyle(element);
            const canScrollY = deltaY !== 0 && /(auto|scroll)/.test(style?.overflowY ?? '') && element.scrollHeight > element.clientHeight;
            const canScrollX = deltaX !== 0 && /(auto|scroll)/.test(style?.overflowX ?? '') && element.scrollWidth > element.clientWidth;

            if (canScrollY || canScrollX) {
                return element;
            }
        }

        return null;
    }

    elementOf(nodeId: number): Element | null {
        const node = this.nodes.getNode(nodeId);

        if (!node) {
            return null;
        }

        return node instanceof Element ? node : node.parentElement;
    }

    private isTextField(element: Element): element is HTMLInputElement | HTMLTextAreaElement {
        return isTextField(element);
    }

    private dispatchPointer(element: Element, type: string, init: PointerEventInit): void {
        if (typeof PointerEvent === 'undefined') {
            return;
        }

        element.dispatchEvent(new PointerEvent(type, init));
    }

    private get view(): (Window & typeof globalThis) | null {
        return this.targetDocument.defaultView;
    }

    // Programmatic edits never fire `change` on blur, yet some fields (FormattedNumberInput) only commit on it.
    private readonly commitRemoteEdit = (event: FocusEvent): void => {
        const field = event.target as Element;

        if (!this.remotelyEditedFields.has(field)) {
            return;
        }

        this.remotelyEditedFields.delete(field);
        field.dispatchEvent(new Event('change', { bubbles: true }));
    };
}
